/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, bidiUtils, propsUtils, keyboardUtils, Bidi } from '@ba-ui-toolkit/utils';
import styles from './TextArea.scss';

const textAreaClassName = styles.textAreaClassName.replace(/"/g, '');
const { resolveBaseTextDir } = bidiUtils;
const { sanitize } = propsUtils;
const { isKey, keyNames } = keyboardUtils;

export default class TextArea extends Component {
	static propTypes = {
		placeholder: PropTypes.string,
		rows: PropTypes.number,
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		disabled: PropTypes.bool,
		readOnly: PropTypes.bool,
		resize: PropTypes.bool,
		fireOnBlur: PropTypes.bool,
		onBlur: PropTypes.func,
		onChange: PropTypes.func,
		dir: PropTypes.oneOf(['rtl', 'ltr']),
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		validationErrorText: PropTypes.string,
		className: PropTypes.string,
		style: PropTypes.object,
		hasValidationError: PropTypes.bool,
	};

	static defaultProps = {
		resize: false,
		className: '',
		style: {},
	};

	state = {
		value: this.props.fireOnBlur ? this.props.value : null,
		showFocusClass: false,
	};

	componentWillReceiveProps({ value }) {
		if (this.props.fireOnBlur && value !== this.props.value) {
			this.setState({ value });
		}
	}

	componentWillUnmount() {
		const { fireOnBlur, onChange, value } = this.props;
		const prevValue = this.state.value;

		// If fireOnBlur and there is an onChange queued then call it before unmount
		// This is to ensure that the value doesn't get lost
		if (fireOnBlur && prevValue !== value) {
			onChange && onChange(prevValue);
		}
	}

	focus() {
		this._inputNode && this._inputNode.focus();
	}

	_onChange = e => {
		const { value } = e.target;
		if (this.props.fireOnBlur) {
			this.setState({ value });
		} else {
			this.props.onChange && this.props.onChange(value, e);
		}
	};

	_onKeyDown = e => {
		if (isKey(e, keyNames.TAB)) this.setState({ showFocusClass: true });
	};

	_onKeyUp = e => {
		if (isKey(e, keyNames.TAB)) this.setState({ showFocusClass: true });
	};

	_onBlur = e => {
		this.setState({ showFocusClass: false });
		if (this.props.fireOnBlur) {
			const { value } = this.state;
			if (value !== this.props.value) {
				this.props.onChange && this.props.onChange(value);
			}
		}
		this.props.onBlur && this.props.onBlur(e);
	};

	_ref = node => {
		this._inputNode = node;
	};

	render() {
		const {
			fireOnBlur,
			disabled,
			readOnly,
			placeholder,
			value,
			rows,
			resize,
			dir,
			textDir,
			validationErrorText,
			hasValidationError,
			className,
			style,
			...others
		} = this.props;

		const classNames = cx(textAreaClassName, 'ba--focusable', {
			noresize: !resize,
			fullHeight: rows == null,
			disabled: disabled || readOnly,
			focus: this.state.showFocusClass,
			error: hasValidationError,
		});

		const textAreaClasses = cx({ noresize: !resize });

		const cProps = {
			onChange: this._onChange,
			onBlur: this._onBlur,
			value: fireOnBlur ? this.state.value : value,
			placeholder,
			disabled,
			readOnly,
			'aria-readonly': readOnly,
			rows,
		};

		return (
			<span
				{...(dir ? { dir } : {})}
				className={`${classNames} ${className}`}
				onKeyUp={this._onKeyUp}
				onKeyDown={this._onKeyDown}
				style={style}
			>
				<textarea
					className={textAreaClasses}
					data-tid="textArea"
					{...sanitize(others, TextArea)}
					{...cProps}
					ref={this._ref}
					dir={value ? resolveBaseTextDir(value, textDir, true) : Bidi.getDir()}
				>
					{value}
				</textarea>
				<span className="validation-error">
					{validationErrorText || 'Validation Error'}
				</span>
			</span>
		);
	}
}
